import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Sparkle, CurrencyDollar, CloudArrowUp, X, ArrowRight } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";

const CONDITIONS = ["Like New", "Good", "Fair", "Well Used"];
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CreateListing() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", category: "", condition: "",
    price_per_day: "", price_per_week: "", price_per_month: "",
    deposit_amount: "", location: "",
    insurance_available: false, delivery_available: false, pickup_available: true,
  });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [aiLoading, setAiLoading] = useState({ desc: false, price: false });
  const [loaded, setLoaded] = useState(!isEdit);

  useEffect(() => {
    api.get("/categories").then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!editId) return;
    api.get(`/items/${editId}`).then(r => {
      const item = r.data;
      setForm({
        title: item.title || "", description: item.description || "",
        category: item.category || "", condition: item.condition || "",
        price_per_day: item.price_per_day != null ? String(item.price_per_day) : "",
        price_per_week: item.price_per_week != null ? String(item.price_per_week) : "",
        price_per_month: item.price_per_month != null ? String(item.price_per_month) : "",
        deposit_amount: item.deposit_amount != null ? String(item.deposit_amount) : "",
        location: item.location || "",
        insurance_available: !!item.insurance_available,
        delivery_available: !!item.delivery_available,
        pickup_available: item.pickup_available !== false,
      });
      setImages(item.images || []);
      setLoaded(true);
    }).catch(() => { toast.error("Item not found"); navigate("/dashboard"); });
  }, [editId, navigate]);

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 6) { toast.error("Max 6 images"); return; }
    setUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages(prev => [...prev, data.path]);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    setUploading(false);
  };

  const removeImage = (idx) => setImages(prev => prev.filter((_, i) => i !== idx));

  const generateDescription = async () => {
    if (!form.title || !form.category || !form.condition) { toast.error("Fill in title, category, and condition first"); return; }
    setAiLoading(p => ({ ...p, desc: true }));
    try {
      const { data } = await api.post("/ai/generate-description", { title: form.title, category: form.category, condition: form.condition, notes: form.description || null });
      set("description", data.description);
      toast.success("Description generated!");
    } catch { toast.error("AI generation failed"); }
    finally { setAiLoading(p => ({ ...p, desc: false })); }
  };

  const suggestPricing = async () => {
    if (!form.title || !form.category || !form.condition) { toast.error("Fill in title, category, and condition first"); return; }
    setAiLoading(p => ({ ...p, price: true }));
    try {
      const { data } = await api.post("/ai/suggest-pricing", { title: form.title, category: form.category, condition: form.condition });
      set("price_per_day", String(data.daily || ""));
      set("price_per_week", String(data.weekly || ""));
      set("price_per_month", String(data.monthly || ""));
      toast.success("Pricing suggested!");
    } catch { toast.error("Pricing suggestion failed"); }
    finally { setAiLoading(p => ({ ...p, price: false })); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category || !form.condition || !form.price_per_day || !form.location) {
      toast.error("Fill in all required fields"); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        price_per_day: parseFloat(form.price_per_day),
        price_per_week: form.price_per_week ? parseFloat(form.price_per_week) : null,
        price_per_month: form.price_per_month ? parseFloat(form.price_per_month) : null,
        deposit_amount: form.deposit_amount ? parseFloat(form.deposit_amount) : 0,
        images,
      };
      if (isEdit) {
        await api.put(`/items/${editId}`, payload);
        toast.success("Listing updated!");
        navigate(`/item/${editId}`);
      } else {
        const { data } = await api.post("/items", payload);
        toast.success("Item listed!");
        navigate(`/item/${data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${isEdit ? "update" : "create"} listing`);
    } finally { setSubmitting(false); }
  };

  if (!loaded) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#2C5545] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-[#FDFBF7] py-8">
      <div className="max-w-2xl mx-auto px-4">
        <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1C1A] tracking-tight mb-2" data-testid="create-listing-heading">
          {isEdit ? "Edit listing" : "List an item"}
        </h1>
        <p className="text-sm text-[#5C665D] mb-8">{isEdit ? "Update your listing details." : "Share what you have. Earn when you're not using it."}</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Images */}
          <div>
            <label className="block text-sm font-medium text-[#1A1C1A] mb-2">Photos (up to 6)</label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {images.map((img, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-[#F5F3EE] border border-[#EAE7E0]">
                  <img src={`${API_URL}/api/files/${img}`} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 w-6 h-6 bg-white/80 rounded-full flex items-center justify-center" data-testid={`remove-image-${i}`}>
                    <X size={12} />
                  </button>
                </div>
              ))}
              {images.length < 6 && (
                <label className="aspect-square rounded-xl border-2 border-dashed border-[#EAE7E0] flex flex-col items-center justify-center cursor-pointer hover:border-[#2C5545] transition-colors" data-testid="upload-image-btn">
                  {uploading ? <div className="w-5 h-5 border-2 border-[#2C5545] border-t-transparent rounded-full animate-spin" /> : <><CloudArrowUp size={24} className="text-[#5C665D] mb-1" /><span className="text-xs text-[#5C665D]">Add Photo</span></>}
                  <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
                </label>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Title *</label>
            <input value={form.title} onChange={e => set("title", e.target.value)} required placeholder="e.g. Vintage Cowboy Hat" className="w-full bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] outline-none transition-all" data-testid="create-title-input" />
          </div>

          {/* Category + Condition */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Category *</label>
              <Select value={form.category} onValueChange={v => set("category", v)}>
                <SelectTrigger className="bg-[#FDFBF7] border-[#EAE7E0] rounded-xl" data-testid="create-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white border-[#EAE7E0] rounded-xl">
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Condition *</label>
              <Select value={form.condition} onValueChange={v => set("condition", v)}>
                <SelectTrigger className="bg-[#FDFBF7] border-[#EAE7E0] rounded-xl" data-testid="create-condition-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white border-[#EAE7E0] rounded-xl">
                  {CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-[#1A1C1A]">Description</label>
              <button type="button" onClick={generateDescription} disabled={aiLoading.desc} className="flex items-center gap-1 text-xs font-medium text-[#D96C4A] hover:text-[#C25939] transition-colors disabled:opacity-50" data-testid="ai-generate-desc-btn">
                {aiLoading.desc ? <div className="w-3 h-3 border border-[#D96C4A] border-t-transparent rounded-full animate-spin" /> : <Sparkle size={14} weight="fill" />} AI Generate
              </button>
            </div>
            <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={5} placeholder="Describe your item..." className="w-full bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] outline-none transition-all resize-none" data-testid="create-description-input" />
          </div>

          {/* Pricing */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-[#1A1C1A]">Pricing *</label>
              <button type="button" onClick={suggestPricing} disabled={aiLoading.price} className="flex items-center gap-1 text-xs font-medium text-[#D96C4A] hover:text-[#C25939] transition-colors disabled:opacity-50" data-testid="ai-suggest-price-btn">
                {aiLoading.price ? <div className="w-3 h-3 border border-[#D96C4A] border-t-transparent rounded-full animate-spin" /> : <CurrencyDollar size={14} weight="fill" />} AI Suggest
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-[#5C665D] mb-1">Per Day *</label>
                <input type="number" step="0.01" min="0" required value={form.price_per_day} onChange={e => set("price_per_day", e.target.value)} placeholder="$0.00" className="w-full bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] transition-all" data-testid="create-price-day-input" />
              </div>
              <div>
                <label className="block text-xs text-[#5C665D] mb-1">Per Week</label>
                <input type="number" step="0.01" min="0" value={form.price_per_week} onChange={e => set("price_per_week", e.target.value)} placeholder="$0.00" className="w-full bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] transition-all" data-testid="create-price-week-input" />
              </div>
              <div>
                <label className="block text-xs text-[#5C665D] mb-1">Per Month</label>
                <input type="number" step="0.01" min="0" value={form.price_per_month} onChange={e => set("price_per_month", e.target.value)} placeholder="$0.00" className="w-full bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] transition-all" data-testid="create-price-month-input" />
              </div>
            </div>
          </div>

          {/* Deposit + Location */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Security Deposit</label>
              <input type="number" step="0.01" min="0" value={form.deposit_amount} onChange={e => set("deposit_amount", e.target.value)} placeholder="$0.00" className="w-full bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] transition-all" data-testid="create-deposit-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Location *</label>
              <input value={form.location} onChange={e => set("location", e.target.value)} required placeholder="City, State" className="w-full bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] transition-all" data-testid="create-location-input" />
            </div>
          </div>

          {/* Options */}
          <div className="flex flex-wrap gap-4">
            {[
              { key: "insurance_available", label: "Offer Insurance" },
              { key: "delivery_available", label: "Offer Delivery" },
              { key: "pickup_available", label: "Offer Pickup" },
            ].map(opt => (
              <label key={opt.key} className="flex items-center gap-2 cursor-pointer" data-testid={`create-${opt.key}-toggle`}>
                <input type="checkbox" checked={form[opt.key]} onChange={e => set(opt.key, e.target.checked)} className="w-4 h-4 rounded border-[#EAE7E0] text-[#2C5545] focus:ring-[#2C5545]" />
                <span className="text-sm text-[#1A1C1A]">{opt.label}</span>
              </label>
            ))}
          </div>

          {/* Submit */}
          <button type="submit" disabled={submitting} className="w-full bg-[#2C5545] text-white hover:bg-[#224235] rounded-full py-3.5 font-medium transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="create-submit-btn">
            {submitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><ArrowRight size={18} weight="bold" /> {isEdit ? "Update Listing" : "List Item"}</>}
          </button>
        </form>
      </div>
    </main>
  );
}
